import type { CSSProperties, ReactNode } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { Check, Combine, Printer, Ruler, X } from 'lucide-react';
import { byRank, rackHeightM, rackWidthM } from '@eombor/contracts';
import { Button, cn } from '@eombor/ui';
import { t } from '../../../shared/i18n/index.js';
import { fillKeyFor, type FillKey } from '../model/fill-color.js';
import { RACK_COLORS } from '../model/rack-colors.js';
import type { LayoutCell, LayoutRack } from '../api/warehouse.api.js';
import { cellStatusLabel, matchesCellFilter, type CellFilter } from '../model/cell-status.js';
import { Select } from '../../../shared/ui/Select.js';
import { cellCodeMeaning } from './CellCode.js';

/**
 * STELLAJ FASADI — javon oldida turgan odam ko'radigan ko'rinish.
 *
 * 3D sahnada oldingi stellaj orqadagisini berkitadi, yozuv burchak ostida
 * siqilib o'qilmay qoladi va kerakli katakka yetish uchun kamerani
 * aylantirish kerak bo'ladi. Kundalik ishda esa savol doim bitta: "shu
 * javonning qaysi katagi bo'sh?". Unga yassi chizma to'g'ridan-to'g'ri javob
 * beradi.
 *
 * CHIZMA MUHANDISLIK CHIZMASI kabi o'qiladi: chapda qatorlar ruletkasi,
 * ikki yonda ramaning ustunlari, har qator ostida balka, tagida esa
 * o'lcham chizig'i. Ilgari rama kataklar ORTIDAGI ko'k to'rtburchak edi
 * va u teshiklar orasidan chiqib, chizmani ko'k chiziqlarga bo'lib
 * yuborardi — temir ko'rinardi, lekin javonga o'xshamasdi.
 *
 * NISBAT HAQIQIY: ustunlar kengligi `bays`, qatorlar balandligi
 * `levelHeights` bo'yicha `fr` birligida beriladi. Ya'ni 1.8 m li proyom
 * ekranda ham 2.7 m li proyomdan tor bo'lib chiqadi va chizma javonning
 * o'ziga o'xshaydi.
 *
 * Ranglar 3D bilan BIR XIL tokenlardan olinadi — ikki ko'rinish bitta
 * omborni ikki xil rangda ko'rsatmasligi kerak.
 */

/**
 * KATAK FONI — YUMSHOQ TINT, to'yingan rang emas.
 *
 * Ilgari katak to'liq `--color-fill-*` rangida bo'yalardi va to'la javon
 * ekranni qip-qizil devorga aylantirardi: matn shu fon ustida o'qilishi
 * qiyin, «qancha to'lgan» esa umuman ko'rinmasdi. Endi rang FONDA
 * yengil (tokenning 30%i sirt bilan aralashgan) va TO'LIQ rang katak
 * tagidagi to'ldirilganlik chizig'iga o'tdi — rang holatni, chiziq esa
 * QANCHASINI aytadi.
 *
 * Tusning o'zi o'sha `--color-fill-*` tokenlaridan: 3D sahna bilan bitta
 * ombor ikki xil rangda ko'rinmaydi.
 *
 * Tailwind yig'ilgan satrni ko'rmaydi (`bg-[var(--color-fill-${key})]`) —
 * shuning uchun sinflar to'liq yozilgan holda turadi.
 */
const FILL_CLASS: Record<FillKey, string> = {
  empty: 'bg-surface border-border-strong',
  low: 'bg-[color-mix(in_oklab,var(--color-fill-low)_30%,var(--color-surface))] border-[color-mix(in_oklab,var(--color-fill-low)_70%,var(--color-border))]',
  mid: 'bg-[color-mix(in_oklab,var(--color-fill-mid)_30%,var(--color-surface))] border-[color-mix(in_oklab,var(--color-fill-mid)_70%,var(--color-border))]',
  high: 'bg-[color-mix(in_oklab,var(--color-fill-high)_26%,var(--color-surface))] border-[color-mix(in_oklab,var(--color-fill-high)_65%,var(--color-border))]',
  /* Bloklangan — chiziqli naqsh: rang emas, SHAKL ham aytadi «tegilmaydi». */
  blocked:
    'border-[var(--color-fill-blocked)] bg-[repeating-linear-gradient(135deg,color-mix(in_oklab,var(--color-fill-blocked)_28%,var(--color-surface))_0_6px,var(--color-surface-2)_6px_12px)]',
};

/** To'ldirilganlik chizig'i — tokenning TO'LIQ rangi. */
const BAR_CLASS: Record<FillKey, string> = {
  empty: 'bg-transparent',
  low: 'bg-[var(--color-fill-low)]',
  mid: 'bg-[var(--color-fill-mid)]',
  high: 'bg-[var(--color-fill-high)]',
  blocked: 'bg-[var(--color-fill-blocked)]',
};

/**
 * Filtrga tushmagan yoki «qayerda?» savoliga javob bo'lmagan katak.
 *
 * `opacity` EMAS: shaffoflik matnni ham xiralashtirib, AA kontrastdan
 * tushirardi. Buning o'rniga rang olib tashlanadi — katak kulrang, lekin
 * kodi o'qiladi.
 */
const QUIET_CLASS = 'bg-surface-2 border-border grayscale';

/** Chizmaning eng kichik balandligi — bir qatorli javon ham chizma bo'lib ko'rinsin. */
const MIN_DRAWING = '19rem';

/**
 * Ruletka va rama ustunining kengligi.
 *
 * Ikkovi ham CSS o'zgaruvchisi bo'lib beriladi, chunki chizma OSTIDAGI
 * o'lcham chizig'i aynan shu ikkisining yig'indisiga surilishi kerak.
 * Taxminiy `padding` yozilsa, ruletkaga uch xonali balandlik tushgan
 * kunda chiziq chizmadan siljib ketardi.
 */
const RULER_W = '3.5rem';
const POST_W = '9px';

/**
 * RAMA USTUNI — javonning ikki yonidagi perfratsiyali temir.
 *
 * Ikki qatlam: pastdagi gradient metallning yaltirog'ini beradi, ustidagi
 * takrorlanuvchi chiziq esa balka o'rnatiladigan TESHIKLARNI. Aynan shu
 * teshiklar javonni javon qilib ko'rsatadi — ularsiz ustun oddiy ko'k
 * chiziq bo'lib qolardi.
 */
export const uprightStyle = {
  backgroundImage: [
    `repeating-linear-gradient(180deg, rgb(0 0 0 / 0.35) 0 3px, transparent 3px 15px)`,
    `linear-gradient(90deg, rgb(0 0 0 / 0.3), transparent 45%, rgb(255 255 255 / 0.22) 60%, rgb(0 0 0 / 0.25))`,
  ].join(','),
  backgroundColor: RACK_COLORS.post,
} as const;

/**
 * BALKA — qator ostidagi tayanch.
 *
 * Yuqori qirrasi yorug', pastkisi qorong'i: yorug'lik tepadan tushadi va
 * shu ikki chiziq yassi to'rtburchakni temir profilga aylantiradi. Ilgari
 * balka katakning `border-bottom` i edi va har katakda uzilib turardi —
 * haqiqiy balka esa javon bo'ylab UZLUKSIZ ketadi.
 */
export const beamStyle = {
  backgroundImage: `linear-gradient(180deg, rgb(255 255 255 / 0.45) 0 1px, ${RACK_COLORS.beam} 1px 3px, rgb(0 0 0 / 0.35) 3px 4px)`,
} as const;

/** Katakning hajmi: tepadan yorug', pastdan soya — mahsulot yotadigan tekislik. */
export const CELL_SHADOW = 'inset 0 1px 0 rgb(255 255 255 / 0.09), inset 0 -2px 3px rgb(0 0 0 / 0.16)';

export interface RackFacadeProps {
  rack: LayoutRack;
  /**
   * Ombordagi barcha javonlar — sarlavhadagi tanlagich uchun. Berilmasa
   * tanlagich chizilmaydi va sarlavha oddiy matn bo'lib qoladi.
   */
  racks?: LayoutRack[];
  onSelectRack?: (rackId: string) => void;
  filter: CellFilter;
  selectedCellId: string | null;
  onSelectCell: (cellId: string) => void;
  /**
   * Etiketka bosish. Berilmasa tanlash rejimi umuman chizilmaydi — ruxsati
   * yo'q odamga bosib bo'lmaydigan tugma ko'rsatilmaydi.
   */
  onPrint?: (cellIds: string[]) => void;
  /**
   * Qatorni o'lchamlarga bo'lish. Berilmasa ruletka o'qiladigan bo'lib
   * qoladi, bosiladigan emas — ruxsati yo'q odamga bosib bo'lmaydigan
   * tugma ko'rsatilmaydi.
   *
   * NEGA CHIZMADA: javonni o'lchash javon oldida turib qilinadi va
   * "qaysi qator" degan savolga chizmaning o'zi javob beradi. Ustaga
   * qaytib borish esa boshqa ekran va boshqa fikr.
   */
  onSplitLevel?: (level: number) => void;
  /**
   * KATAKLARNI BIRLASHTIRISH — javondagi ajratgich olib tashlandi.
   *
   * Tanlash rejimi etiketka bilan BIR XIL: odam javon chizmasiga qarab
   * turib qo'shni o'rinlarni bosadi. Ikkinchi rejim yasash shu bitta
   * ishni ikki xil qilib o'rgatardi.
   */
  onMerge?: (cellIds: string[]) => void;
  /**
   * Hali SERVERGA YUBORILMAGAN joylashtirishlar, katak `id` si bo'yicha.
   * Chizma faqat server holatini biladi — busiz savatga qo'shilgan
   * mahsulot keyingi mahsulotga o'tganda chizmada ko'rinmay qolardi va
   * skladchi o'sha katakka ikkinchi narsani tiqib yuborardi.
   */
  pending?: ReadonlyMap<string, readonly FacadePendingItem[]>;
  /** Katakdagi kutilayotgan joylashtirishlarni bekor qilish (× tugmasi). */
  onRemovePending?: (cellId: string) => void;
  /**
   * «SHU MAHSULOT SHU YERDA» — katak `id` si bo'yicha tayyor matn (`56 dona`).
   *
   * `pending` dan ATAYLAB alohida. `pending` — hali yuborilmagani, uzuq
   * chiziq bilan chiziladi va uni bekor qilish mumkin; bu esa SERVERDAGI
   * haqiqat: mahsulot hozir o'sha katakda turibdi.
   *
   * Berilganda qolgan kataklar so'nadi. Savol bitta — «qayerda?» — va
   * javobi ko'zga birdan tashlanishi kerak; yonidagi o'ttiz katak to'liq
   * rangda tursa, javob ular orasida yo'qolardi.
   */
  marked?: ReadonlyMap<string, string>;
  /**
   * Qidiruvda TOPILGAN katak — skaner yoki «Topish» dan. Chizma uni
   * ko'rinishga suradi va halqa bilan bir necha marta «chaqiradi»: ko'z
   * o'ttiz katak orasidan qidirmasin.
   */
  foundCellId?: string | null;
  /** Chizma ostidagi belgilar (legend). Sukut bo'yicha chiziladi. */
  legend?: boolean;
  className?: string;
}

/** Katakka qo'yilgan, lekin hali yuborilmagan mahsulot. */
export interface FacadePendingItem {
  key: string;
  name: string;
  /** Tayyor matn: `80 dona`. */
  qty: string;
}

const NO_PENDING: readonly FacadePendingItem[] = [];

const freeCount = (rack: LayoutRack): number =>
  rack.cells.filter((cell) => cell.status === 'FREE').length;

export const rackLabel = (rack: LayoutRack): string =>
  rack.name === null || rack.name === '' ? rack.code : `${rack.code} — ${rack.name}`;

/** Kataklarni qatorlarga ajratadi, YUQORI QATOR TEPADA — javonning o'zi kabi. */
export const byLevel = (cells: readonly LayoutCell[], levels: number): LayoutCell[][] => {
  const rows: LayoutCell[][] = Array.from({ length: levels }, () => []);
  for (const cell of cells) {
    const row = rows[cell.level - 1];
    if (row !== undefined) row.push(cell);
  }
  return rows.map((row) => [...row].sort((a, b) => a.idx - b.idx)).reverse();
};

/** Ekran birligi — SANTIMETR, usta bilan bir xil. */
export const cm = (metres: number): string => `${Math.round(metres * 100)}`;

/** Katak kodining fasaddagi qisqa shakli: `04-02`. */
export const shortCode = (level: number, idx: number): string =>
  `${String(level).padStart(2, '0')}-${String(idx).padStart(2, '0')}`;

export const RackFacade = ({
  rack,
  racks,
  onSelectRack,
  filter,
  selectedCellId,
  onSelectCell,
  onPrint,
  onSplitLevel,
  onMerge,
  pending,
  onRemovePending,
  marked,
  foundCellId = null,
  legend = true,
  className,
}: RackFacadeProps) => {
  const reduceMotion = useReducedMotion() === true;
  const drawingRef = useRef<HTMLDivElement>(null);

  /* Topilgan katak ko'rinishga keladi — gorizontal siljiydigan chizmada
     ham, uzun sahifada ham. `nearest`: ko'rinib turgan bo'lsa sahifa
     joyidan qimirlamaydi. */
  useEffect(() => {
    if (foundCellId === null) return;
    const node = drawingRef.current?.querySelector<HTMLElement>(`[data-cell-id="${CSS.escape(foundCellId)}"]`);
    node?.scrollIntoView({ block: 'nearest', inline: 'center', behavior: reduceMotion ? 'auto' : 'smooth' });
  }, [foundCellId, rack.id, reduceMotion]);

  const levels = rack.levelHeights.length;
  const rows = useMemo(() => byLevel(rack.cells, levels), [rack.cells, levels]);
  const free = freeCount(rack);

  /**
   * TANLASH REJIMI.
   *
   * Bosishdan oldingi savol har safar boshqacha: yangi javonga hamma katak
   * kerak, buzilgan etiketkani almashtirishga esa bittasi. Shu sababli
   * tanlov aynan FASADDA qilinadi — odam javonning chizmasiga qarab turib,
   * kerakli qatorni yoki o'rinni bosadi va nima bosilishini ko'rib turadi.
   *
   * Rejim ataylab alohida: rejimsiz har bosish katakni tanlashi ham,
   * belgilashi ham kerak bo'lardi va ikki ma'no bir tugmaga sig'maydi.
   */
  const [picking, setPicking] = useState(false);
  const [picked, setPicked] = useState<ReadonlySet<string>>(() => new Set());

  const stopPicking = (): void => {
    setPicking(false);
    setPicked(new Set());
  };

  /** Filtr qo'yilgan bo'lsa "hamma" va "qator" aynan ko'rinib turganlarni bildiradi. */
  const visible = (cells: readonly LayoutCell[]): LayoutCell[] =>
    cells.filter((cell) => matchesCellFilter(cell.status, filter));

  const toggleCells = (cells: readonly LayoutCell[]): void =>
    setPicked((current) => {
      const next = new Set(current);
      const all = cells.length > 0 && cells.every((cell) => current.has(cell.id));
      for (const cell of cells) {
        if (all) next.delete(cell.id);
        else next.add(cell.id);
      }
      return next;
    });

  /**
   * Etiketkalar JAVON TARTIBIDA chiqadi — pastki qatordan boshlab, chapdan
   * o'ngga. Bosilgan dasta shu tartibda tursa, skladchi javon bo'ylab bir
   * marta yurib chiqadi; bosish tartibida bo'lsa har etiketkada qaerga
   * yopishtirishni qidirib turardi.
   */
  const inRackOrder = (ids: ReadonlySet<string>): string[] =>
    rack.cells
      .filter((cell) => ids.has(cell.id))
      .sort((a, b) => a.level - b.level || a.idx - b.idx)
      .map((cell) => cell.id);

  /**
   * Tanlov birlashtirishga YARAYDIMI — yaramasa, SABABI.
   *
   * Qoida serverda ham bor (`planCellMerge`) va o'sha yerda hal qiluvchi.
   * Bu yerdagisi nusxa emas, IZOH: o'chiq turgan tugma «nega?» degan
   * savol qoldiradi, javobini esa faqat bosib ko'rgandan keyin aytish
   * kech bo'ladi.
   */
  const mergeBlocker = (): string | null => {
    if (picked.size < 2) return t('warehouse.mergeNeedTwo');
    const chosen = rack.cells.filter((cell) => picked.has(cell.id));
    const level = chosen[0]?.level ?? 0;
    if (chosen.some((cell) => cell.level !== level)) return t('warehouse.mergeOneLevel');

    const busy = chosen.find((cell) => cell.status === 'OCCUPIED' || cell.fillPercent > 0);
    if (busy !== undefined) return t('warehouse.mergeBusy', { code: shortCode(busy.level, busy.idx) });

    const row = byRank(rack.cells.filter((cell) => cell.level === level));
    const ranks = chosen
      .map((cell) => row.findIndex((item) => item.id === cell.id))
      .sort((a, b) => a - b);
    const first = ranks[0] ?? 0;
    if (!ranks.every((rank, offset) => rank === first + offset)) {
      return t('warehouse.mergeAdjacent');
    }
    return null;
  };

  /** Qator ruletkasining bitta bo'g'ini — bosiladigan yoki shunchaki o'qiladigan. */
  const rulerRow = (level: number, cells: LayoutCell[]) => {
    const all = cells.length > 0 && cells.every((cell) => picked.has(cell.id));
    const interactive = picking || onSplitLevel !== undefined;

    const selectedAll = all && picking;

    /* QATOR RAQAMI — BADGE. Ilgari 12px xira raqam edi va skladchi qaysi
       qator qaysi ekanini chizmadan tez o'qiy olmasdi. Endi to'q brend
       fonida oq, qalin raqam; belgilanganda urg'u rangiga o'tadi. Ostida
       qator balandligi (sm) — ikkinchi darajali, kichik va xira. */
    const body = (
      <>
        <span
          className={cn(
            'tabular relative flex size-8 items-center justify-center rounded-[var(--radius-sm)]',
            'text-base leading-none font-bold shadow-[var(--shadow-1)]',
            'transition-[background-color,box-shadow] duration-[var(--motion-fast)]',
            selectedAll
              ? 'bg-accent text-accent-contrast ring-accent/30 ring-2'
              : 'bg-primary text-primary-contrast',
          )}
        >
          {level}
          {selectedAll ? (
            <span className="bg-surface text-accent absolute -top-1.5 -right-1.5 flex size-4 items-center justify-center rounded-full shadow-[var(--shadow-1)]">
              <Check size={10} strokeWidth={3} aria-hidden />
            </span>
          ) : null}
        </span>
        <span className="text-text-muted flex items-center gap-0.5 text-[0.6875rem] leading-none tabular-nums">
          {interactive && !picking ? <Ruler size={10} aria-hidden className="opacity-70" /> : null}
          {cm(rack.levelHeights[level - 1] ?? 0)}
        </span>
      </>
    );

    const shell = cn(
      'flex min-h-0 flex-1 flex-col items-center justify-center gap-1 rounded-l-[var(--radius-sm)]',
      'transition-colors duration-[var(--motion-fast)]',
    );

    /* Qatorning OSTIDA — chizmadagi balkaning davomi, xuddi o'sha 4px:
       badge ustuni chizma bilan qatorma-qator bir chiziqda qoladi va
       qavatlarni ajratuvchi chiziq raqamgacha yetib keladi. */
    const beam = (
      <div className="flex h-[4px] shrink-0 justify-end" aria-hidden>
        <div className="w-2.5" style={beamStyle} />
      </div>
    );

    const rowStyle = { flexGrow: rack.levelHeights[level - 1] ?? 1, flexBasis: 0 };

    if (!interactive) {
      return (
        <div key={level} className="flex min-h-0 flex-col" style={rowStyle}>
          <div className={shell} aria-hidden>
            {body}
          </div>
          {beam}
        </div>
      );
    }

    return (
      <div key={level} className="flex min-h-0 flex-col" style={rowStyle}>
        <button
          type="button"
          /* Ko'rinadigan matn — raqam va santimetr, ya'ni chizmaning
             ruletkasi. Ekran o'quvchi esa amalni eshitishi kerak: "2-qator"
             belgilash, "2-qatorni o'lchash" bo'lish uchun. */
          aria-label={
            picking
              ? t('warehouse.levelLabel', { level })
              : t('warehouse.levelSplitAction', { level })
          }
          {...(picking ? { 'aria-pressed': all } : {})}
          onClick={() => (picking ? toggleCells(cells) : onSplitLevel?.(level))}
          className={cn(
            shell,
            'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-accent)]',
            selectedAll ? 'bg-accent-soft' : 'hover:bg-surface-2',
          )}
        >
          {body}
        </button>
        {beam}
      </div>
    );
  };

  return (
    <div className={cn('flex min-w-0 flex-col gap-3', className)}>
      <header className="flex flex-wrap items-end justify-between gap-x-4 gap-y-2">
        {/*
          Javon TANLAGICH bilan almashadi va har birining yonida bo'sh o'rin
          soni turadi. Sababi: ekran bitta javonni ko'rsatadi va "bu javon
          to'la" degan javob yetarli emas — odamning haqiqiy savoli "bo'sh
          joy QAYERDA?" va unga javob ro'yxatning o'zida bo'lishi kerak.
        */}
        {racks === undefined || onSelectRack === undefined || racks.length < 2 ? (
          <h3 className="text-text text-base font-medium">{rackLabel(rack)}</h3>
        ) : (
          <Select
            label={t('warehouse.facadeRack')}
            value={rack.id}
            options={racks.map((item) => ({
              value: item.id,
              label: `${rackLabel(item)} · ${t('warehouse.facadeFreeShort', { free: freeCount(item) })}`,
            }))}
            onChange={(event) => {
              // Tanlov javon bilan birga ketadi: aks holda boshqa javonning
              // kataklariga etiketka bosilib ketardi.
              stopPicking();
              onSelectRack(event.target.value);
            }}
            className="min-w-[16rem]"
          />
        )}

        <div className="flex flex-wrap items-center gap-3">
          {/* Kub metr emas, O'RIN: skladchi javonni shu birlikda o'ylaydi.

              «QAYERDA?» rejimida esa BO'SH o'rinlar soni umuman boshqa
              savolning javobi. Bu yerda u yordam bermaydi, aksincha:
              odam «10 tasi bo'sh» ni o'qib, o'zi qidirayotgan songa
              aloqador deb o'ylab qoladi. */}
          {marked !== undefined && marked.size > 0 ? null : (
            <FreeMeter free={free} total={rack.cells.length} />
          )}

          {(onPrint === undefined && onMerge === undefined) || picking ? null : (
            <Button size="sm" variant="secondary" onClick={() => setPicking(true)}>
              <Printer size={14} aria-hidden />
              {/* Birlashtirish ham shu rejimdan boshlanadi, ya'ni yozuv
                  faqat etiketka haqida gapira olmaydi. */}
              {onMerge === undefined ? t('warehouse.facadePick') : t('warehouse.facadePickAny')}
            </Button>
          )}
        </div>
      </header>

      {/* Juda keng ekranda (1920+) chizma markazda va cheklangan: kataklar
          haddan tashqari cho'zilsa, «javon» emas, chiziqlar to'plami bo'lib
          qoladi. */}
      <div ref={drawingRef} className="min-w-0 overflow-x-auto pb-1 [scrollbar-width:thin]">
        <div
          className="mx-auto flex w-full max-w-[72rem] min-w-[30rem] flex-col gap-1.5"
          style={{ '--facade-ruler': RULER_W, '--facade-post': POST_W } as CSSProperties}
        >
          <div className="flex items-stretch">
            {/* QATORLAR RULETKASI — chizmaning chap chekkasi. Har doim
                ko'rinadi: qaysi qator ekani chizmaning o'zidan o'qilishi
                kerak, tugma bosilganda emas. */}
            {/* Qatorlar chizmadagidek QURILADI (bir xil `flex-grow` va ostida
                4px balka) — ilgari bu `grid` 3px oraliq bilan edi va raqamlar
                balkalardan asta-sekin siljib, qatorga emas, oraliqqa tushardi. */}
            <div
              className="flex w-[var(--facade-ruler)] shrink-0 flex-col"
              style={{ minHeight: MIN_DRAWING }}
            >
              {rows.map((row, rowIndex) => rulerRow(levels - rowIndex, visible(row)))}
            </div>

            {/* RAMA: chap ustun — qatorlar — o'ng ustun. */}
            <div className="flex min-w-0 flex-1 items-stretch">
              <div className="w-[var(--facade-post)] shrink-0 rounded-t-[2px]" style={uprightStyle} aria-hidden />

              <div
                className="bg-surface-2 flex min-w-0 flex-1 flex-col"
                style={{ minHeight: MIN_DRAWING }}
              >
                {/*
                  TO'R EMAS, QATORLAR RO'YXATI.
                  Ilgari bu bitta CSS `grid` edi va ustunlar hamma qator uchun
                  BIR XIL bo'lishga majbur qilardi. Endi har qator o'zicha
                  bo'linadi: 1-qator uchga, 2-qator butun bo'lishi mumkin va
                  to'g'ri to'rtburchak to'r buni ifodalay olmaydi.
                */}
                {rows.map((row, rowIndex) => {
                  const level = levels - rowIndex;
                  return (
                    <div
                      key={level}
                      className="flex min-h-0 flex-col"
                      // Qator balandligi HAQIQIY nisbatda: 50 sm li qator
                      // 100 sm likdan ikki baravar past ko'rinadi.
                      style={{ flexGrow: rack.levelHeights[level - 1] ?? 1, flexBasis: 0 }}
                    >
                      <div className="flex min-h-0 flex-1 gap-[2px]">
                        {/* `bay` — katakning qatordagi O'RNI (0 dan).
                            Kodidagi raqam EMAS: birlashtirilgan qatorda
                            raqam tushib qolgan bo'ladi, proyom esa
                            chapdan sanaladi. `byLevel` allaqachon
                            `idx` bo'yicha tartiblab bergan. */}
                        {row.map((cell, bay) => {
                          const here = marked?.get(cell.id);
                          /*
                           * IKKI XIL SO'NISH, ikki xil sabab.
                           *
                           * `dimmed` — filtrga tushmagan katak: u baribir
                           * chizmaning bir qismi va rangi (to'lganligi)
                           * o'qilishi kerak.
                           *
                           * `muted` — «qayerda?» savoliga javob BO'LMAGAN
                           * katak. Bu yerda rang ortiqcha: qizil va yashil
                           * kataklar yonma-yon tursa, javob ularning
                           * orasida yo'qolardi. Shuning uchun rangi
                           * butunlay olinadi va faqat javob rangli qoladi.
                           */
                          const dimmed = !matchesCellFilter(cell.status, filter);
                          const muted =
                            marked !== undefined && marked.size > 0 && here === undefined;
                          const chosen = picked.has(cell.id);
                          const active = picking ? chosen : cell.id === selectedCellId;
                          const widthCm = cm(rack.bays[level - 1]?.[bay] ?? 0);
                          const heightCm = cm(rack.levelHeights[level - 1] ?? 0);
                          const waiting = pending?.get(cell.id) ?? NO_PENDING;
                          const firstWaiting = waiting[0];
                          const removable =
                            waiting.length > 0 && onRemovePending !== undefined && !picking;
                          const fill = fillKeyFor(cell);
                          const found = cell.id === foundCellId && !picking;
                          const quiet = muted || dimmed;

                          return (
                            /* O'ram — × tugmasi katak TUGMASINING ichida
                               bo'lolmaydi (tugma ichida tugma HTML da
                               taqiqlangan), shuning uchun yonma-yon turadi. */
                            <div
                              key={cell.id}
                              className="group/cell relative flex min-w-0"
                              style={{
                                /* O'RIN KENGLIGI HAQIQIY nisbatda: 130 sm li
                                   o'rin ekranda ham 30 sm likdan to'rt baravar
                                   keng. `flexBasis: 0` shart — busiz katak o'z
                                   MATNI kengligida qolib, qatorning qolgan
                                   qismi bo'sh bo'lib turardi. */
                                flexGrow: rack.bays[level - 1]?.[bay] ?? 1,
                                flexBasis: 0,
                                minWidth: 0,
                              }}
                            >
                            <button
                              type="button"
                              style={{ boxShadow: CELL_SHADOW }}
                              aria-pressed={active}
                              // Ekran o'quvchi kodni EMAS, uning ma'nosini o'qiydi.
                              aria-label={[
                                cell.code,
                                cellCodeMeaning(cell.code) ?? '',
                                cellStatusLabel(cell.status),
                                here ?? '',
                                cell.topItem ?? '',
                                ...cell.reservedFor.map((item) =>
                                  t('warehouse.cellReservedBadge', { name: item.name }),
                                ),
                                ...waiting.map((item) =>
                                  t('warehouse.facadePendingItem', { name: item.name, qty: item.qty }),
                                ),
                              ]
                                .filter((part) => part !== '')
                                .join(' · ')}
                              /* Hover da HAMMA kutilayotgan mahsulotlar — katakka
                                 bittasi va "+2" sig'adi, ro'yxat esa shu yerda. */
                              title={
                                [
                                  cellCodeMeaning(cell.code) ?? '',
                                  /* Katakdagi mahsulot — chizmada 2 qatorga
                                     qirqiladi, to'liq nomi shu yerda. */
                                  cell.topItem ?? '',
                                  cell.itemKinds > 1
                                    ? t('warehouse.facadeMoreKinds', { count: cell.itemKinds - 1 })
                                    : '',
                                  ...waiting.map((item) => `${item.name} — ${item.qty}`),
                                  // Ajratilgani eng pastda turadi va sig'masa kesiladi.
                                  ...cell.reservedFor.map((item) =>
                                    t('warehouse.cellReservedBadge', { name: item.name }),
                                  ),
                                ]
                                  .filter((part) => part !== '')
                                  .join('\n') || undefined
                              }
                              onClick={() =>
                                picking ? toggleCells([cell]) : onSelectCell(cell.id)
                              }
                              className={cn(
                                /* Satrlar SIQILMAYDI (`shrink-0`) va markaz
                                   "xavfsiz": sig'masa pastdan kesiladi. Ilgari
                                   band katakda satrlar siqilib, eng birinchi —
                                   KOD — 6px ga tushib ko'rinmay qolardi. Endi
                                   kod doim tepada, ortiqchasi (o'lcham) pastda
                                   birinchi bo'lib yo'qoladi. */
                                '@container relative flex min-w-0 flex-1 flex-col gap-1',
                                'overflow-hidden rounded-[4px] border px-1 pt-1 pb-1 text-center',
                                'transition-[outline-color,box-shadow,filter,scale] duration-[var(--motion-fast)]',
                                'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-accent)]',
                                quiet ? QUIET_CLASS : FILL_CLASS[fill],
                                quiet ? undefined : 'hover:shadow-[var(--shadow-2)] active:scale-[0.98]',
                                active
                                  ? /* TANLANGAN — eng kuchli belgi: qalin urg'u
                                       halqasi va ko'tarilgan soya. */
                                    'z-10 outline-[3px] outline-offset-[-1px] outline-[var(--color-accent)] shadow-[var(--shadow-2)]'
                                  : found || here !== undefined
                                    ? /* Topilgan — TUTASH yashil chiziq: "mahsulot
                                         hozir shu yerda turibdi". */
                                      'z-10 outline-[3px] outline-offset-[-1px] outline-[var(--color-success)]'
                                    : waiting.length > 0
                                      ? /* Kutilayotgan — uzuq chiziq: "bu yerga
                                           qo'yildi, lekin hali yuklanmadi". */
                                        'outline-2 outline-offset-[-2px] outline-[var(--color-accent)] outline-dashed'
                                      : undefined,
                              )}
                              data-cell-id={cell.id}
                              data-found={found ? 'true' : undefined}
                            >
                              {chosen ? (
                                <span
                                  aria-hidden
                                  className={cn(
                                    'absolute top-0.5 right-0.5 flex h-4 w-4 items-center justify-center',
                                    'rounded-full bg-[var(--color-accent)] text-[var(--color-accent-contrast)]',
                                  )}
                                >
                                  <Check size={11} />
                                </span>
                              ) : null}

                              {/* «TOPILDI» — halqa bir necha marta tarqaladi va
                                  to'xtaydi: ko'z katakni topadi, lekin chizma
                                  doimiy miltillamaydi. Harakatsiz rejimda halqa
                                  yo'q — yashil chiziqning o'zi yetarli. */}
                              {found && !reduceMotion ? (
                                <motion.span
                                  key={`pulse-${foundCellId ?? ''}`}
                                  aria-hidden
                                  className="pointer-events-none absolute inset-0 rounded-[4px] ring-4 ring-[var(--color-success)]"
                                  initial={{ opacity: 0.85, scale: 1 }}
                                  animate={{ opacity: 0, scale: 1.12 }}
                                  transition={{ duration: 1.1, ease: 'easeOut', repeat: 3 }}
                                />
                              ) : null}

                              {/* MATN BLOKI o'z ichida kesiladi: sig'maganini pastki
                                  o'lchagich ustiga chiqarib, «ustidan chizilgan»
                                  matnga aylantirmaydi. */}
                              <span className="flex min-h-0 w-full flex-1 flex-col items-center justify-center-safe gap-0.5 overflow-hidden [&>*]:shrink-0">
                              <span className="tabular w-full truncate font-mono text-xs leading-4 font-semibold text-text">
                                {shortCode(level, cell.idx)}
                              </span>

                              {/* TOPILGAN MIQDOR — kodning ostida, hamma
                                  narsadan oldin. «Qayerda va qancha?» degan
                                  savolning ikkinchi yarmi shu yerda va u
                                  katakdagi boshqa matnlarga siqib qo'yilmasligi
                                  kerak. */}
                              {here === undefined ? null : (
                                <span className="text-success hidden w-full truncate text-[0.6875rem] font-semibold tabular-nums @min-[3.5rem]:block">
                                  {here}
                                </span>
                              )}

                              {/* NIMA TURGANI — kodning ostida, eng ko'zga tashlanadigan
                                  joyda. Ilgari bu yerda faqat foiz bor edi va skladchi
                                  "45%" dan nima yotganini bilolmasdi: har katakni ochib
                                  ko'rishga to'g'ri kelardi.

                                  TOR KATAKDA YASHIRINADI (`@container`): 16 sm li
                                  o'rinda nom baribir bitta harfgacha qirqilardi va
                                  o'sha bitta harf kodni pastga surib yuborardi. */}
                              {cell.topItem === null ? null : (
                                <span className="hidden w-full @min-[4.5rem]:block">
                                  <span className="line-clamp-2 text-[0.8125rem] leading-4 font-medium break-words text-text">
                                    {cell.topItem}
                                  </span>
                                </span>
                              )}

                              {/* TARTIB muhimlik bo'yicha: nima turgani, qancha turgani,
                                  keyin ajratilgani. Sig'masa pastdagisi kesiladi.
                                  «va yana N xil» — CHIP: bu matn emas, sanoq. */}
                              {cell.itemKinds > 1 ? (
                                <span className="hidden rounded-full border border-border bg-surface/80 px-1.5 text-[0.6875rem] leading-4 font-medium text-text-muted @min-[4.5rem]:inline-block">
                                  {t('warehouse.facadeMoreKinds', { count: cell.itemKinds - 1 })}
                                </span>
                              ) : null}

                              {/* SAVATDAGI mahsulot — nomi va MIQDORI. Bir nechta
                                  bo'lsa birinchisi va "+N"; to'liq ro'yxat
                                  `title` da. */}
                              {firstWaiting === undefined ? null : (
                                <span className="text-accent hidden w-full flex-col items-center @min-[4.5rem]:flex">
                                  <span className="w-full truncate text-[0.6875rem] font-semibold">
                                    {firstWaiting.name}
                                  </span>
                                  <span className="text-[0.625rem] font-medium tabular-nums">
                                    {firstWaiting.qty}
                                    {waiting.length > 1
                                      ? ` · ${t('warehouse.facadeReservedMore', { count: waiting.length - 1 })}`
                                      : ''}
                                  </span>
                                </span>
                              )}

                              <span className="hidden text-xs leading-4 text-text-muted tabular-nums @min-[3.5rem]:block">
                                {cell.fillPercent > 0 ? (
                                  <span className="font-semibold text-text">{cell.fillPercent}%</span>
                                ) : null}
                                {cell.fillPercent > 0 ? ' · ' : ''}
                                {widthCm}×{heightCm}
                              </span>

                              {/* AJRATILGAN katak — bo'sh bo'lsa ham nima uchunligi
                                  ko'rinib tursin. Aynan shu holat muhim: bo'sh
                                  katakka boshqa mahsulot qo'yish eng oson bo'ladi.

                                  Bittadan ko'p bo'lsa BIRINCHISI va sanoq: uch
                                  nomni tor katakka tiqish uchalasini ham
                                  o'qib bo'lmaydigan qilardi. */}
                              {cell.reservedFor.length === 0 ? null : (
                                <span className="text-accent hidden w-full items-baseline justify-center gap-1 @min-[4.5rem]:flex">
                                  <span className="truncate text-[0.625rem] font-medium italic">
                                    {t('warehouse.cellReservedBadge', {
                                      name: cell.reservedFor[0]?.name ?? '',
                                    })}
                                  </span>
                                  {cell.reservedFor.length > 1 ? (
                                    <span className="shrink-0 text-[0.5625rem] tabular-nums opacity-80">
                                      {t('warehouse.facadeReservedMore', {
                                        count: cell.reservedFor.length - 1,
                                      })}
                                    </span>
                                  ) : null}
                                </span>
                              )}

                              </span>

                              {/* TO'LGANLIK O'LCHAGICHI — katak tagidagi chiziq.
                                  Rang holatni aytadi, chiziq esa QANCHASINI:
                                  "45%" va "80%" bir xil orange bo'lib
                                  ko'rinardi va farqni faqat o'qib bilish
                                  mumkin edi. */}
                              {/* Iz (track) doim chiziladi — bo'sh katakda ham «bu yerda
                                  o'lchagich bor, u nolda» deb o'qiladi. */}
                              {cell.status === 'BLOCKED' ? null : (
                                <span
                                  aria-hidden
                                  className="mx-0.5 h-1 shrink-0 overflow-hidden rounded-full bg-[color-mix(in_oklab,var(--color-text)_10%,transparent)]"
                                >
                                  <span
                                    style={{ width: `${Math.min(Math.max(cell.fillPercent, 0), 100)}%` }}
                                    className={cn(
                                      'block h-full rounded-full',
                                      quiet ? 'bg-text-muted' : BAR_CLASS[fill],
                                    )}
                                  />
                                </span>
                              )}
                            </button>

                            {/* × — faqat hover da (va klaviatura fokusida): doim
                                ko'rinsa, har band katakda qizil nuqta bo'lib
                                chizmani shovqinga to'ldirardi. */}
                            {removable ? (
                              <button
                                type="button"
                                aria-label={t('warehouse.facadePendingRemove', { code: cell.code })}
                                title={t('warehouse.facadePendingRemove', { code: cell.code })}
                                onClick={() => onRemovePending(cell.id)}
                                className={cn(
                                  'absolute top-0.5 right-0.5 z-20 flex size-5 items-center justify-center',
                                  'bg-surface text-danger rounded-full shadow-[var(--shadow-1)]',
                                  'opacity-0 transition-opacity duration-[var(--motion-fast)]',
                                  'group-hover/cell:opacity-100 focus-visible:opacity-100',
                                  'hover:bg-danger hover:text-danger-contrast',
                                  'focus-visible:outline-2 focus-visible:outline-[var(--color-accent)]',
                                )}
                              >
                                <X size={12} strokeWidth={2.5} aria-hidden />
                              </button>
                            ) : null}
                            </div>
                          );
                        })}
                      </div>

                      {/* BALKA — qatorning OSTIDA, javon bo'ylab uzluksiz. */}
                      <div className="h-[4px] shrink-0" style={beamStyle} aria-hidden />
                    </div>
                  );
                })}
              </div>

              <div className="w-[var(--facade-post)] shrink-0 rounded-t-[2px]" style={uprightStyle} aria-hidden />
            </div>
          </div>

          {/*
            O'LCHAM CHIZIG'I — chizmaning tagida, uchlarida ilmoq bilan.
            Ilgari bu yerda shunchaki "200 × 110 × 200 sm" yozuvi turardi
            va u qaysi o'lcham qayerga tegishli ekanini aytmasdi.
          */}
          <div className="flex items-center gap-3 pl-[calc(var(--facade-ruler)+var(--facade-post))]">
            <div className="relative h-3 min-w-0 flex-1" aria-hidden>
              <span className="bg-border-strong absolute top-1/2 right-0 left-0 h-px" />
              <span className="bg-border-strong absolute top-0 bottom-0 left-0 w-px" />
              <span className="bg-border-strong absolute top-0 right-0 bottom-0 w-px" />
              <span className="bg-bg text-text-faint tabular absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 px-2 text-[0.625rem]">
                {cm(rackWidthM(rack))}
              </span>
            </div>

            <p className="text-text-faint tabular shrink-0 text-[0.625rem]">
              {t('warehouse.facadeDepthHeight', {
                depth: cm(rack.depthM),
                height: cm(rackHeightM(rack)),
              })}{' '}
              {t('builder.unitCm')}
            </p>
          </div>
        </div>
      </div>

      {legend ? (
        <FacadeLegend blocked={rack.cells.some((cell) => cell.status === 'BLOCKED')} />
      ) : null}

      {picking && (onPrint !== undefined || onMerge !== undefined) ? (
        <div className="bg-surface-2 flex flex-wrap items-center justify-between gap-2 rounded-[var(--radius-sm)] px-3 py-2">
          <p className="text-text-muted text-sm tabular-nums">
            {t('warehouse.facadePickCount', { count: picked.size })}
          </p>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="ghost" onClick={() => toggleCells(visible(rack.cells))}>
              {t('warehouse.facadePickAll')}
            </Button>
            <Button size="sm" variant="ghost" onClick={stopPicking}>
              {t('warehouse.cancel')}
            </Button>
            {onMerge === undefined ? null : (
              <Button
                size="sm"
                variant="secondary"
                disabled={mergeBlocker() !== null}
                title={mergeBlocker() ?? undefined}
                onClick={() => onMerge(inRackOrder(picked))}
              >
                <Combine size={14} aria-hidden />
                {t('warehouse.mergeCells')}
              </Button>
            )}
            {onPrint === undefined ? null : (
              <Button
                size="sm"
                disabled={picked.size === 0}
                onClick={() => onPrint(inRackOrder(picked))}
              >
                <Printer size={14} aria-hidden />
                {t('warehouse.printLabels')}
              </Button>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
};

/**
 * BO'SH O'RINLAR HISOBLAGICHI — badge va ingichka chiziq.
 *
 * Son o'zi yetmaydi: «24 dan 12» va «24 dan 2» bir xil matn bo'lib
 * o'qiladi. Chiziq bo'sh joy ULUSHINI ko'z bilan ko'rsatadi; to'la javonda
 * u bo'sh qoladi va bu xato emas — javon haqiqatan to'la.
 */
const FreeMeter = ({ free, total }: { free: number; total: number }) => {
  const share = total === 0 ? 0 : Math.round((free / total) * 100);
  return (
    <div className="flex min-w-[11rem] flex-col gap-1.5">
      <p className="flex items-center gap-2 text-sm text-text-muted tabular-nums">
        <span
          className={cn(
            'inline-flex h-6 min-w-6 items-center justify-center rounded-full px-2 text-xs font-semibold',
            free === 0 ? 'bg-surface-2 text-text-muted ring-1 ring-border' : 'bg-success-soft text-success',
          )}
        >
          {free}
        </span>
        {t('warehouse.facadeSlots', { total, free })}
      </p>
      <span
        role="meter"
        aria-label={t('warehouse.facadeSlots', { total, free })}
        aria-valuemin={0}
        aria-valuemax={total}
        aria-valuenow={free}
        className="h-1.5 w-full overflow-hidden rounded-full bg-surface-2 ring-1 ring-border/60"
      >
        <span
          className="block h-full rounded-full bg-success transition-[width] duration-[var(--motion-slow)] ease-[var(--ease-standard)]"
          style={{ width: `${share}%` }}
        />
      </span>
    </div>
  );
};

/** Belgining namunasi — chizmadagi katak bilan AYNAN bir xil sinflar. */
const Swatch = ({ className, children }: { className?: string; children?: ReactNode }) => (
  <span
    aria-hidden
    className={cn('relative inline-block h-4 w-6 shrink-0 overflow-hidden rounded-[3px] border', className)}
  >
    {children}
  </span>
);

/**
 * CHIZMA BELGILARI. Rang ma'nosi chizmaning o'zidan o'qilishi kerak —
 * yangi skladchi «qizil — xato» deb o'ylamasin: qizil «to'la» degani.
 */
const FacadeLegend = ({ blocked }: { blocked: boolean }) => (
  <ul
    aria-label={t('warehouse.legendLabel')}
    className="flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-border pt-3 text-xs text-text-muted"
  >
    <li className="flex items-center gap-1.5">
      <Swatch className={FILL_CLASS.empty} />
      {t('warehouse.legendEmpty')}
    </li>
    <li className="flex items-center gap-1.5">
      <Swatch className="border-[color-mix(in_oklab,var(--color-fill-mid)_70%,var(--color-border))]">
        <span className="absolute inset-y-0 left-0 w-1/2 bg-[color-mix(in_oklab,var(--color-fill-low)_30%,var(--color-surface))]" />
        <span className="absolute inset-y-0 right-0 w-1/2 bg-[color-mix(in_oklab,var(--color-fill-mid)_30%,var(--color-surface))]" />
      </Swatch>
      {t('warehouse.legendPartial')}
    </li>
    <li className="flex items-center gap-1.5">
      <Swatch className={FILL_CLASS.high} />
      {t('warehouse.legendFull')}
    </li>
    {blocked ? (
      <li className="flex items-center gap-1.5">
        <Swatch className={FILL_CLASS.blocked} />
        {t('warehouse.legendBlocked')}
      </li>
    ) : null}
    <li className="flex items-center gap-1.5">
      <Swatch className="border-transparent bg-surface outline-[3px] outline-offset-[-3px] outline-[var(--color-accent)]" />
      {t('warehouse.legendSelected')}
    </li>
    <li className="flex items-center gap-1.5">
      <Swatch className="border-transparent bg-surface outline-[3px] outline-offset-[-3px] outline-[var(--color-success)]" />
      {t('warehouse.legendFound')}
    </li>
  </ul>
);
